import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, X, Loader2, Users, GraduationCap, Building2, Lock, Crown, Sparkles, Upload } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useCareerHistory } from '@/hooks/useCareerHistory';
import { getTierFromProfile } from '@/lib/tier-utils';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { ResumeUploadDialog } from '@/components/settings/ResumeUploadDialog';
import { ResumePreviewConfirm } from '@/components/settings/ResumePreviewConfirm';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';
import { BETA_CONFIG } from '@/config/beta';
import type { MentorProfile, MentorshipScope } from '@/types/mentorship';

interface MentorProfileFormProps {
  profile: MentorProfile | null;
  organization?: string;
  onSave: (profile: Partial<MentorProfile>) => Promise<boolean>;
}

export function MentorProfileForm({ profile: mentorProfile, organization, onSave }: MentorProfileFormProps) {
  const { profile: userProfile } = useProfile();
  const { workHistory } = useCareerHistory();
  const [saving, setSaving] = useState(false);
  const [isAvailableAsMentor, setIsAvailableAsMentor] = useState(false);
  const [isSeekingMentor, setIsSeekingMentor] = useState(false);
  const [bio, setBio] = useState('');
  const [skillsCanTeach, setSkillsCanTeach] = useState<string[]>([]);
  const [skillsWantToLearn, setSkillsWantToLearn] = useState<string[]>([]);
  const [yearsExperience, setYearsExperience] = useState<string>('');
  const [maxMentees, setMaxMentees] = useState('3');
  const [meetingFrequency, setMeetingFrequency] = useState('weekly');
  const [mentorshipScope, setMentorshipScope] = useState<MentorshipScope>('both');
  const [newTeachSkill, setNewTeachSkill] = useState('');
  const [newLearnSkill, setNewLearnSkill] = useState('');
  const userTier = getTierFromProfile(userProfile);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [resumeData, setResumeData] = useState<any>(null);

  const isPro = BETA_CONFIG.allFeaturesUnlocked || userTier === 'pro' || userTier === 'beta_pro';

  useEffect(() => {
    if (mentorProfile) {
      setIsAvailableAsMentor(mentorProfile.is_available_as_mentor);
      setIsSeekingMentor(mentorProfile.is_seeking_mentor);
      setBio(mentorProfile.bio || '');
      setSkillsCanTeach(mentorProfile.skills_can_teach);
      setSkillsWantToLearn(mentorProfile.skills_want_to_learn);
      setYearsExperience(mentorProfile.years_experience?.toString() || '');
      setMaxMentees(mentorProfile.max_mentees.toString());
      setMeetingFrequency(mentorProfile.preferred_meeting_frequency);
      setMentorshipScope(mentorProfile.mentorship_scope || 'both');
    }
  }, [mentorProfile]);

  const handleAddTeachSkill = () => {
    if (newTeachSkill.trim() && !skillsCanTeach.includes(newTeachSkill.trim())) {
      setSkillsCanTeach([...skillsCanTeach, newTeachSkill.trim()]);
      setNewTeachSkill('');
    }
  };

  const handleAddLearnSkill = () => {
    if (newLearnSkill.trim() && !skillsWantToLearn.includes(newLearnSkill.trim())) {
      setSkillsWantToLearn([...skillsWantToLearn, newLearnSkill.trim()]);
      setNewLearnSkill('');
    }
  };

  const handleRemoveTeachSkill = (skill: string) => {
    setSkillsCanTeach(skillsCanTeach.filter(s => s !== skill));
  };

  const handleRemoveLearnSkill = (skill: string) => {
    setSkillsWantToLearn(skillsWantToLearn.filter(s => s !== skill));
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'teach' | 'learn') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'teach') {
        handleAddTeachSkill();
      } else {
        handleAddLearnSkill();
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      is_available_as_mentor: isAvailableAsMentor,
      is_seeking_mentor: isSeekingMentor,
      bio,
      skills_can_teach: skillsCanTeach,
      skills_want_to_learn: skillsWantToLearn,
      years_experience: yearsExperience ? parseInt(yearsExperience) : null,
      max_mentees: parseInt(maxMentees),
      preferred_meeting_frequency: meetingFrequency,
      mentorship_scope: mentorshipScope,
    });
    setSaving(false);
  };

  const handleGenerateBio = async () => {
    setGeneratingBio(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const careerContext = workHistory?.length
        ? workHistory.slice(0, 3).map(w => `${w.title} at ${w.company}`).join(', ')
        : '';

      const prompt = `Write a concise 2-3 sentence mentorship bio for me. It should sound professional but warm, highlighting what I bring and what I'm looking for.${
        userProfile?.job_role ? ` Current role: ${userProfile.job_role}.` : ''
      }${userProfile?.target_job_role ? ` Target role: ${userProfile.target_job_role}.` : ''
      }${userProfile?.skills?.length ? ` Skills: ${userProfile.skills.join(', ')}.` : ''
      }${careerContext ? ` Recent experience: ${careerContext}.` : ''
      }${skillsCanTeach.length ? ` I can teach: ${skillsCanTeach.join(', ')}.` : ''
      }${skillsWantToLearn.length ? ` I want to learn: ${skillsWantToLearn.join(', ')}.` : ''
      } Do NOT include any greeting or preamble — just the bio text.`;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/career-coach`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ type: 'freetext_chat', message: prompt }),
        }
      );

      if (!response.ok) throw new Error('Failed to generate bio');

      const data = await response.json();
      const generatedBio = data.data?.response?.trim();
      if (generatedBio) setBio(generatedBio);
    } catch (err) {
      reportError(err, 'ai-coach', { action: 'generate-mentor-bio' });
    } finally {
      setGeneratingBio(false);
    }
  };

  const handleResumeComplete = () => {
    // After resume is confirmed and saved to career history, auto-populate skills
    if (resumeData?.skills?.length) {
      const newSkills = resumeData.skills.filter((s: string) => !skillsCanTeach.includes(s));
      if (newSkills.length) {
        setSkillsCanTeach(prev => [...prev, ...newSkills.slice(0, 10)]);
      }
    }
    setResumeData(null);
  };

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle>Mentorship Profile</CardTitle>
        <CardDescription>
          Set up your mentorship preferences to connect with others
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Availability Toggles */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Offer Mentorship - Pro only */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${!isPro ? 'bg-muted/30 border-dashed' : 'bg-muted/50 border-border'}`}>
            <div className="flex items-center gap-3">
              {!isPro ? (
                <Lock className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Users className="w-5 h-5 text-primary" />
              )}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  Offer Mentorship
                  {!isPro && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Crown className="w-3 h-3" />
                      Pro
                    </Badge>
                  )}
                </Label>
                <p className="text-xs text-muted-foreground">Help others grow</p>
              </div>
            </div>
            {!isPro ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Switch
                        checked={false}
                        disabled
                        onClick={() => setUpgradeModalOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Upgrade to Pro to offer mentorship</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Switch
                checked={isAvailableAsMentor}
                onCheckedChange={setIsAvailableAsMentor}
              />
            )}
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-accent" />
              <div>
                <Label className="text-sm font-medium">Seek Mentorship</Label>
                <p className="text-xs text-muted-foreground">Find a mentor</p>
              </div>
            </div>
            <Switch
              checked={isSeekingMentor}
              onCheckedChange={setIsSeekingMentor}
            />
          </div>
        </div>

        {/* Upgrade prompt for free users */}
        {!isPro && (
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-start gap-3">
              <Crown className="w-5 h-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Upgrade to Pro to offer mentorship</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pro members can offer mentorship and connect with mentees to share their expertise.
                </p>
                <Button 
                  variant="link" 
                  className="h-auto p-0 mt-2 text-primary"
                  onClick={() => setUpgradeModalOpen(true)}
                >
                  Upgrade to Pro →
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bio */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="bio">About You</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleGenerateBio}
              disabled={generatingBio}
              className="text-xs gap-1.5 h-7"
            >
              {generatingBio ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {generatingBio ? 'Writing...' : 'Generate with AI'}
            </Button>
          </div>
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell others about your background and experience..."
            rows={3}
          />
          <button
            type="button"
            onClick={() => setShowResumeUpload(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Upload className="w-3 h-3" />
            Upload resume to enrich your profile
          </button>
        </div>

        {/* Skills Can Teach */}
        {isAvailableAsMentor && (
          <div className="space-y-2">
            <Label>Skills You Can Teach</Label>
            <div className="flex gap-2">
              <Input
                value={newTeachSkill}
                onChange={(e) => setNewTeachSkill(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'teach')}
                placeholder="e.g., Data Analysis, Leadership..."
              />
              <Button type="button" variant="secondary" onClick={handleAddTeachSkill}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {skillsCanTeach.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {skillsCanTeach.map((skill) => (
                  <Badge key={skill} variant="secondary" className="gap-1">
                    {skill}
                    <button onClick={() => handleRemoveTeachSkill(skill)}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Skills Want to Learn */}
        {isSeekingMentor && (
          <div className="space-y-2">
            <Label>Skills You Want to Learn</Label>
            <div className="flex gap-2">
              <Input
                value={newLearnSkill}
                onChange={(e) => setNewLearnSkill(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'learn')}
                placeholder="e.g., Public Speaking, Strategy..."
              />
              <Button type="button" variant="secondary" onClick={handleAddLearnSkill}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {skillsWantToLearn.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {skillsWantToLearn.map((skill) => (
                  <Badge key={skill} variant="outline" className="gap-1">
                    {skill}
                    <button onClick={() => handleRemoveLearnSkill(skill)}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mentorship Scope - shown if either mentor or mentee */}
        {(isAvailableAsMentor || isSeekingMentor) && (
          <div className="space-y-3 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <Label>Organization Preference</Label>
            </div>
            {organization ? (
              <p className="text-sm text-muted-foreground">
                Your organization: <span className="font-medium text-foreground">{organization}</span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Set your organization in Settings to enable org-based matching
              </p>
            )}
            <RadioGroup
              value={mentorshipScope}
              onValueChange={(value) => setMentorshipScope(value as MentorshipScope)}
              className="grid gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="internal" id="scope-internal" />
                <Label htmlFor="scope-internal" className="font-normal cursor-pointer">
                  Same organization only
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="external" id="scope-external" />
                <Label htmlFor="scope-external" className="font-normal cursor-pointer">
                  Different organization only
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both" id="scope-both" />
                <Label htmlFor="scope-both" className="font-normal cursor-pointer">
                  Open to all (recommended)
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Mentor-specific settings */}
        {isAvailableAsMentor && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="experience">Years of Experience</Label>
              <Input
                id="experience"
                type="number"
                min="0"
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
                placeholder="e.g., 5"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxMentees">Max Mentees</Label>
              <Select value={maxMentees} onValueChange={setMaxMentees}>
                <SelectTrigger id="maxMentees">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} {n === 1 ? 'mentee' : 'mentees'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Meeting Frequency</Label>
              <Select value={meetingFrequency} onValueChange={setMeetingFrequency}>
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="as-needed">As needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save Mentorship Profile
        </Button>

        <UpgradeModal
          isOpen={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          trigger="mentor_message"
        />
      </CardContent>

      <ResumeUploadDialog
        open={showResumeUpload}
        onOpenChange={setShowResumeUpload}
        onParsed={(data) => setResumeData(data)}
      />
      <ResumePreviewConfirm
        open={!!resumeData}
        onOpenChange={(open) => { if (!open) setResumeData(null); }}
        data={resumeData || { skills: [], work_history: [], education: [], certifications: [] }}
        onComplete={handleResumeComplete}
      />
    </Card>
  );
}
